import React, { useState, useEffect } from 'react';
import TextField from '@mui/material/TextField';
import Dialog from '@mui/material/Dialog';
import DialogActions from '@mui/material/DialogActions';
import DialogContent from '@mui/material/DialogContent';
import DialogTitle from '@mui/material/DialogTitle';
import Radio from '@mui/material/Radio';
import RadioGroup from '@mui/material/RadioGroup';
import { Autocomplete } from '@mui/material';
import Checkbox from '@mui/material/Checkbox';
import FormControl from '@mui/material/FormControl';
import FormControlLabel from '@mui/material/FormControlLabel';
import FormLabel from '@mui/material/FormLabel';
import Snackbar from '@mui/material/Snackbar';
import MuiAlert from '@mui/material/Alert';
import { KitButton } from 'components/common/cms-kit';

// local imports
import PleaseWait from 'components/common/PleaseWait';
import { callApi, BadRequestError } from 'services/apiWrapper';
import { formatDate } from 'services/stringUtils';
import { logClientException } from 'appinsights/clientAppInsights';
import { FEATURE_SWITCH_TYPES } from 'services/constants';
import { InstitutionWithSubscription } from 'types/InstitutionTypes';
import { FeatureSwitchEnum } from 'types/FetureSwitchTypes';

const FEATURE_SWITCH_VALUE_MAX_LENGTH = 1000;

interface Institution {
  name: string;
  institutionType: string;
  subscriptionId: string;
  subscriptionProduct: string;
  subscriptionEndDate: string;
}

interface FeatureSwitchDialogState {
  switch_name: string;
  switch_type: string;
  enabled: boolean;
  value: string;
  valueError?: boolean;
  subscriptions?: Institution[];
  switch_nameError?: boolean;
  subscriptionsError?: boolean;
  canSave?: boolean;
}

interface AddFeatureSwitchDialogProps {
  action: 'Add' | 'Update';
  open: boolean;
  user: Record<string, any>;
  editFeatureSwitch?: {
    switch_name: string;
    switch_type: string;
    enabled: boolean;
    value: string;
  };
  onClose: (doSave: boolean) => void;
  subList: Array<InstitutionWithSubscription>;
}

const Alert = React.forwardRef<HTMLDivElement, any>(function Alert(props, ref) {
  return <MuiAlert elevation={6} ref={ref} variant="filled" {...props} />;
});

const defaultErrorMessage =
  'An unexpected error has occurred. Please try again.';

const AddFeatureSwitchDialog: React.FC<AddFeatureSwitchDialogProps> = (
  props,
) => {
  const [errorMessage, setErrorMessage] = useState<string | null>(null);
  const [isLoading, setIsLoading] = useState(false);
  const [featureSwitchDialogState, setFeatureSwitchDialogState] =
    useState<FeatureSwitchDialogState>({
      switch_name: '',
      switch_type: 'Basic',
      enabled: true,
      value: '',
      subscriptions: [],
    });

  const memoizedSubList = React.useMemo(() => props.subList, [props.subList]);

  useEffect(() => {
    const init = () => {
      if (props.editFeatureSwitch) {
        setFeatureSwitchDialogState({
          switch_name: props.editFeatureSwitch.switch_name,
          switch_type: props.editFeatureSwitch.switch_type,
          enabled: props.editFeatureSwitch.enabled,
          value: props.editFeatureSwitch.value,
          subscriptions: [],
        });
      } else {
        resetState();
      }
    };

    if (props.open) {
      init();
    }
  }, [props.open, props.editFeatureSwitch]);

  useEffect(() => {
    if (
      props.open &&
      props.editFeatureSwitch &&
      props.editFeatureSwitch.switch_type === FeatureSwitchEnum.Subscription
    ) {
      const subscriptions = props.editFeatureSwitch.value?.split(',') || [];
      const selectedSubs = subscriptions.map((sub) =>
        props.subList.find((inst) => inst.subscriptionId === sub),
      );
      setFeatureSwitchDialogState((prevState) => ({
        ...prevState,
        subscriptions: selectedSubs.filter(Boolean) as Institution[],
      }));
    }
  }, [props.subList, props.editFeatureSwitch, props.open]);

  const resetState = () => {
    setErrorMessage(null);
    setFeatureSwitchDialogState({
      switch_name: '',
      switch_type: 'Basic',
      enabled: true,
      value: '',
      subscriptions: [],
    });
  };

  const canSaveFeatureSwitch = (): boolean => {
    let hasError = false;
    const featureSwitch = { ...featureSwitchDialogState };

    if (!featureSwitch.switch_name) {
      hasError = true;
      featureSwitch.switch_nameError = true;
    }

    if (
      featureSwitch.switch_type === FeatureSwitchEnum.Subscription &&
      (!featureSwitch.subscriptions || featureSwitch.subscriptions.length === 0)
    ) {
      hasError = true;
      featureSwitch.subscriptionsError = true;
    } else if (
      featureSwitch.switch_type !== FeatureSwitchEnum.Subscription &&
      featureSwitch.switch_type !== FeatureSwitchEnum.Basic &&
      !featureSwitch.value
    ) {
      hasError = true;
      featureSwitch.valueError = true;
    }

    if (featureSwitch.switch_type === 'Percentage') {
      const valueNum = Number(featureSwitch.value);
      if (
        featureSwitch.value === '' ||
        isNaN(valueNum) ||
        !isFinite(valueNum)
      ) {
        hasError = true;
        featureSwitch.valueError = true;
      } else {
        featureSwitch.valueError = false;
      }
    }

    setFeatureSwitchDialogState({ ...featureSwitch, canSave: !hasError });
    return !hasError;
  };

  const closeFeatureSwitchDialog = async (doSave: boolean) => {
    if (!doSave) {
      props.onClose(doSave);
      resetState();
      return;
    }

    if (!canSaveFeatureSwitch()) {
      return;
    }

    const featureSwitch = { ...featureSwitchDialogState };
    delete featureSwitch.switch_nameError;
    delete featureSwitch.subscriptionsError;
    delete featureSwitch.canSave;

    if (
      featureSwitch.switch_type === FeatureSwitchEnum.Subscription &&
      featureSwitch.subscriptions
    ) {
      featureSwitch.value = featureSwitch.subscriptions
        .map((sub) => sub.subscriptionId)
        .join(',');
      delete featureSwitch.subscriptions;
    }

    if (featureSwitch.switch_type === FeatureSwitchEnum.UserList) {
      featureSwitch.value = featureSwitch.value.toLowerCase().trim();
    }

    try {
      setIsLoading(true);
      await callApi(
        '/api/db/feature-switch',
        props.action === 'Add' ? 'POST' : 'PUT',
        featureSwitch,
      );
      resetState();
      props.onClose(doSave);
    } catch (error) {
      logClientException(error);
      if (error instanceof BadRequestError) {
        setErrorMessage(error.message);
      } else {
        setErrorMessage(defaultErrorMessage);
      }
    } finally {
      setIsLoading(false);
    }
  };

  const featureSwitchOnChange = (
    field: keyof FeatureSwitchDialogState,
    value: any,
  ) => {
    let _canSave = true;
    let fieldError: Boolean = false;
    if (field === 'switch_name' && !value) {
      fieldError = true;
      _canSave = false;
    }

    setFeatureSwitchDialogState((prevState) => ({
      ...prevState,
      [field]: value,
      [`${field}Error`]: fieldError,
      canSave: true,
    }));
  };

  const handleAlertClose = (
    _event: React.SyntheticEvent<Element, Event> | Event,
    reason?: string,
  ) => {
    if (reason === 'clickaway') {
      return;
    }
    setErrorMessage(null);
  };

  return (
    <Dialog
      maxWidth="sm"
      scroll="paper"
      fullWidth
      open={props.open}
      onClose={() => closeFeatureSwitchDialog(false)}
    >
      <DialogTitle>{props.action} Feature Switch</DialogTitle>
      <DialogContent>
        <Snackbar
          open={!!errorMessage}
          onClose={handleAlertClose}
          anchorOrigin={{ vertical: 'top', horizontal: 'center' }}
        >
          <Alert
            onClose={handleAlertClose}
            severity="error"
            sx={{ width: '100%' }}
          >
            {errorMessage}
          </Alert>
        </Snackbar>
        <TextField
          disabled={props.action === 'Update'}
          value={featureSwitchDialogState.switch_name}
          error={!!featureSwitchDialogState.switch_nameError}
          autoFocus
          required
          margin="dense"
          id="switch_name"
          label="Feature Switch name"
          type="text"
          fullWidth
          variant="outlined"
          onChange={(e) =>
            featureSwitchOnChange('switch_name', e.target.value.trim())
          }
          slotProps={{
            htmlInput: { maxLength: 100 },
            inputLabel: { shrink: true }
          }} />
        <FormControlLabel
          control={
            <Checkbox
              checked={featureSwitchDialogState.enabled}
              onChange={(e) =>
                featureSwitchOnChange('enabled', e.target.checked)
              }
            />
          }
          label="Enabled"
        />
        <FormControl fullWidth sx={{ marginTop: '7px' }}>
          <FormLabel id="feature-switch-type-label">Code Type</FormLabel>
          <RadioGroup
            aria-labelledby="feature-switch-type-label"
            name="feature-switch-type"
            value={featureSwitchDialogState.switch_type}
            onChange={(e) =>
              featureSwitchOnChange('switch_type', e.target.value)
            }
          >
            {FEATURE_SWITCH_TYPES.map((c) => (
              <FormControlLabel
                key={c.switch_name}
                value={c.switch_name}
                control={<Radio />}
                label={c.label}
              />
            ))}
          </RadioGroup>
        </FormControl>
        {featureSwitchDialogState.switch_type ===
          FeatureSwitchEnum.Subscription && (
          <Autocomplete
            sx={{ marginTop: '7px' }}
            multiple
            options={memoizedSubList}
            getOptionLabel={(option) =>
              `${option.name} (${option.institutionType})`
            }
            renderOption={(props, option) => (
              <li {...props}>
                {option.name} ({option.institutionType}) (
                {option.subscriptionProduct} -{' '}
                {formatDate(option.subscriptionEndDate)})
              </li>
            )}
            renderInput={(params) => (
              <TextField
                required
                {...params}
                error={!!featureSwitchDialogState.subscriptionsError}
                variant="outlined"
                label="Subscriptions"
                placeholder="Subscriptions"
              />
            )}
            isOptionEqualToValue={(o, v) =>
              o.subscriptionId === v?.subscriptionId
            }
            fullWidth
            value={featureSwitchDialogState.subscriptions}
            onChange={(_, value) =>
              featureSwitchOnChange('subscriptions', value)
            }
          />
        )}
        {featureSwitchDialogState.switch_type !==
          FeatureSwitchEnum.Subscription && (
          <TextField
            value={featureSwitchDialogState.value}
            error={!!featureSwitchDialogState.valueError}
            margin="dense"
            id="value"
            label="Value"
            required={
              featureSwitchDialogState.switch_type === 'Percentage' ||
              featureSwitchDialogState.switch_type === 'User List'
            }
            type={
              featureSwitchDialogState.switch_type === 'Percentage'
                ? 'number'
                : 'text'
            }
            fullWidth
            variant="outlined"
            onChange={(e) => featureSwitchOnChange('value', e.target.value)}
            slotProps={{
              htmlInput: { maxLength: FEATURE_SWITCH_VALUE_MAX_LENGTH },
              inputLabel: { shrink: true }
            }} />
        )}
        <PleaseWait isLoading={isLoading} />
      </DialogContent>
      <DialogActions>
        <KitButton
          onClick={() => closeFeatureSwitchDialog(false)}
          round
          size="sm"
        >
          Cancel
        </KitButton>
        <KitButton
          onClick={() => closeFeatureSwitchDialog(true)}
          round
          size="sm"
          color="success"
          disabled={!featureSwitchDialogState.canSave}
        >
          {props.action}
        </KitButton>
      </DialogActions>
    </Dialog>
  );
};

export default AddFeatureSwitchDialog;
